import {IReg, REG} from "/@lib@/commons/registry.js";
import {AccelKeyMgr, ACTION, IAccelKeyMgrPointer, IAction} from "/@lib@/commons/actions.js";
import {EFolderState, GridColTreeDef, GridDataHolderJsonTree} from "/@back@/commons/widgets/tree.js";
import {EGridDropPos, GridColDef, IGrid, IGridLineDrawer, IGridRowDropMgr} from "/@back@/commons/widgets/grid-core.js";
import {IInfo, IInfoBroker, IInfoConsumer} from "/@lib@/commons/infos.js";
import {IActionable, IContextMenuActionsPointer, IFocusActionables} from "/@back@/commons/actionables.js";
import {Grid, OGridSmallInit} from "/@back@/commons/widgets/grid-tags.js";
import {IDepotShortResUiEnv, IDepotUiEnv, JStoreChange} from "/@lib@/store/depot.js";
import {isNodeAutoFold, JNodeAutoFold, JNodeShortProps, JNodeTree, JSearchNodesResult, URLTREE, UrlTreeSrv} from "/@lib@/store/urlTree.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {IDepotCtx, IDepotShortResCtx, InfoCurrentRes, InfoReqCurrentRes, RES, resPath} from "/@lib@/store/res.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {CellBuilderResIconName, redrawResLine} from "/@back@/store/widgets/resGridColumns.js";
import {ActionBtn} from "/@back@/commons/widgets/buttons.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {BaseElementAsync, BASIS, OSkinableInit} from "/@back@/commons/basis.js";
import {GridDataRowJson} from "/@back@/commons/widgets/grid-libs.js";
import {OLastDatasInit} from "/@lib@/commons/lastDatas.js";
import {LANG} from "/@lib@/commons/lang.js";
import {BarActions, OBarActionsInit} from "/@back@/commons/widgets/bars.js";
import {EventMgr} from "/@lib@/commons/events.js";
import {isRespError} from "/@lib@/commons/io/io.js";
import {AddByImport} from "/@back@/store/actions/resActions.js";
import {getFilesOnDrop} from "/@lib@/commons/io/files.js";


/**
 * Widget UtBrowser : arbre des ressources d'un dépot avec filtre.
 * Aucune gestion des versions : la na
 */
export interface UtBrowser extends BaseElementAsync {
	initialize(init: OUtBrowserInit): this
}

export interface OUtBrowserInit extends OSkinableInit, OLastDatasInit {

	reg?: IReg<IDepotUiEnv>
	autoCloseReg?: boolean

	fetcher?: IUtBrowserFetcher

	/** Path racine de l'arbre. "" par défaut. */
	pathRoot?: string

	/** Actions associées aux items sélectionnés (toolbar et menu contextuel). */
	actions?: IAction<IUtBrowserCtx>[]

	/** Racourcis claviers à capter sur la sel courante. */
	accelKeyMgr?: AccelKeyMgr<IUtBrowserCtx>

	/** Fonction de construction custom du contenu quand le dépot est vide. */
	emptyTree?: (utBrowser: UtBrowser) => Node;

	/** Redéfinition de la colonne primaire. */
	primaryCol?: GridColTreeDef

	/** Colonnes secondaires à ajouter du grid. */
	secondaryCols?: GridColDef[]

	/** InfoBroker destiné à dispatcher les events pour les actions de ce UtBrowser. */
	infoBroker?: IInfoBroker

	/** Réagira aux infos issues de ce broker (InfoCurrentRes) */
	resHandlingReact?: IInfoBroker

	/** Sur une InfoCurrentRes la res courante est une marque sur la ligne ou un chgt de sélection. */
	resHandlingMode?: 'mark' | 'sel'

	/** Appelé sur un chgt de sélection. */
	onSelChange?: (utBrowser: UtBrowser, sel: JNodeShortProps[]) => void;

	/**
	 * Action exécutée au double-click ou sur les frappes entrée.
	 */
	defaultAction?: IAction<IUtBrowserCtx>

	/** Masque le filtre textuel. */
	hideSearch?: boolean

	/** Toolbar à gauche */
	actionsStart?: OBarActionsInit<IUtBrowserCtx | any>

	/** Toolbar à droite */
	actionsEnd?: OBarActionsInit<IUtBrowserCtx | any>

	/** Restreint l'affichage aux noeuds respectant ce filtre. */
	nodeFilter?: (res: JNodeShortProps) => boolean

	/** Surcharge des options par défaut du grid de cet UtBrowser. */
	grid?: OGridSmallInit
}

export interface IUtBrowserFetcher {
	fetchNode(path: resPath, withChildren?: boolean): Promise<JNodeUtBrowser | null>

	/** Récupération partielle des fils d'un dossier. */
	fetchFoldedChildren(path: resPath, from: string, to?: string): Promise<JNodeUtBrowser[]>

	/** Récupération partielle des dossiers d'un noeud VCB. */
	fetchVersions(path: resPath): Promise<JNodeUtBrowser[]>

	searchNodes(root: resPath, exp: string, from?: resPath | undefined): Promise<JSearchNodesResult>

	/**
	 * Algo de tri naturel des paths de l'urlTree.
	 * Aujourd'hui égal à nameSortFn, sufffisant dans les usages, mais formellement
	 * on pourrait vouloir un algo combinant nameSortFn et versionSortFn.
	 */
	naturalSortFn: (this: void, path1: resPath, path2: resPath) => number

	/**
	 * Algo de tri naturel des noms des noeuds de l'urlTree.
	 */
	nameSortFn: (this: void, n1: string, n2: string) => number

	/** Algo de tri naturel des versions de l'urlTree */
	versionSortFn: (this: void, v1: string, v2: string) => number
}

export interface JNodeTreeB extends JNodeTree {
	ch?: JNodeUtBrowser[] | null

	/** En cas de VCB ce noeud réprésente-t-il le nom de la res ou une de ses versions (fils du nom). */
	vcb?: 'n' | 'v'
}

export type JNodeUtBrowser = JNodeTreeB | JNodeAutoFold;

/**
 * Contexte pour les actions du UtBrowser.
 * Abstraction de UtBrowser pour être utilisé en contexte d'actions wrappées/surchagées.
 */
export interface IUtBrowserCtx extends IDepotCtx, IDepotShortResCtx {
	/**
	 * Le reg pour l'execution des actions est surchargé en IDepotShortResUiEnv avec en node courant :
	 * - la ressource sélectionnée si une et une seule res est sélectionnée,
	 * - la resource racine de l'arbre sinon.
	 * Si la racine de l'arbre est non chargée ou absente, le reg est celui de IDepotUiEnv,
	 * donc les props de IDepotShortResUiEnv (path, nodeInfos, resType) sont undefined.
	 */
	reg: IReg<IDepotUiEnv & IDepotShortResUiEnv>

	emitter: Grid

	utBrowser: UtBrowser
}

export class UtBrowser extends BaseElementAsync implements IGridLineDrawer, IInfoConsumer, IFocusActionables<IDepotCtx>, IContextMenuActionsPointer<IDepotCtx>, IAccelKeyMgrPointer<IDepotCtx> {
	reg: IReg<IDepotUiEnv>;
	autoCloseReg?: boolean

	pathRoot: string;

	infoBroker: IInfoBroker;

	resHandling: IInfoBroker;

	accelKeyMgr?: AccelKeyMgr<IUtBrowserCtx>;

	emptyTree: (utBrowser: UtBrowser) => Node;

	onSelChange?: EventMgr<(utBrowser: UtBrowser, sel: JNodeShortProps[]) => void>;

	/** Contexte pour les actions. */
	actContext: IUtBrowserCtx;

	/** boutons à afficher dans la focusToolbar. */
	focusActionables?: IActionable<IUtBrowserCtx>[];

	/** Actions associées aux items sélectionnés (mémoire pour le menuContextuel). */
	actions?: IAction<IUtBrowserCtx>[];

	grid: Grid;
	search: HTMLInputElement;

	datasFull: UtBrowserDataFull;

	datasSearch: UtBrowserDataSearch;

	get currentDatas(): UtBrowserDataBase | undefined {return this.grid?.dataHolder as UtBrowserDataBase}

	protected resHandlingBySel: boolean

	protected async _initialize(init: OUtBrowserInit) {
		this.reg = init.reg;
		if (init.autoCloseReg) this.autoCloseReg = true;
		this.pathRoot = init.pathRoot || URLTREE.DEFAULT_PATH_ROOT;
		this.infoBroker = init.infoBroker;
		this.resHandling = init.resHandlingReact;
		this.emptyTree = init.emptyTree;
		this.onSelChange = new EventMgr();
		if (init.onSelChange) this.onSelChange.add(init.onSelChange);

		this.datasFull = new UtBrowserDataFull(init.fetcher || new UtBrowserFetcher(this.reg.env.universe.adminUrlTree).setFilter(this.reg.getPref("utBrowser.filter")), this.reg, this.pathRoot, init.nodeFilter);
		this.datasFull.callbackFetching = this;

		this.resHandlingBySel = init.resHandlingMode === "sel";

		const sr = this._attach(this.localName, init);
		this.grid = new Grid();

		this.actContext = {
			//Propriétés fixes
			emitter: this.grid,
			utBrowser: this,
			infoBroker: this.infoBroker,
			//Propriétés contextualisés en fonction de la sel.
			// Sélection en cours limité aux Nodes de l'urlTree (ie sans les JNodeAutoFold).
			resList: [],
			// Registre du type de res sélectionnée ou du type du noeud racine de l'arbre.
			reg: null,
		}
		this._refreshActContext();

		const colDefs: GridColDef[] = [init.primaryCol || new GridColTreeDef('resTree')
			.setFlex('1rem', 1, 1).setMinWidth('55px')
			.setRowDropMgr(rowDropMgr)
			.setOnDblClick(init.defaultAction ? "none" : "toggle")
			.setCellBuilder(new CellBuilderUtBrowser(this.reg))];
		if (init.secondaryCols) colDefs.push(...init.secondaryCols);

		const gridInit: OGridSmallInit = {
			selType: init.resHandlingMode === "sel" ? 'monoClick' : 'multi',
			columnDefs: colDefs,
			dataHolder: this.datasFull,
			hideHeaders: true,
			lineDrawer: this,
			skinOver: 'store-ut-browser/grid',
			skinScroll: 'scroll/small',
			noResizableCol: true,
			emptyBody: (): Node => {
				const datas = this.currentDatas;
				if (datas?.lastError === undefined) {
					return <c-msg label="Chargement..." level="info"/>
				} else if (datas.lastError === null) {
					if (this.modeSearch) return <c-msg label="Aucun contenu ne correspond à votre filtre" level="info"/>
					return this.emptyTree ? this.emptyTree(this) : <c-msg label="Aucun contenu" level="info"/>
				} else if (isRespError(datas.lastError) && datas.lastError.response.status === 403) {
					return <c-msg label="Accès non autorisé" level="error"/>
				} else {
					return <c-msg label="Accès au serveur impossible" level="error"/>
				}
			},
			defaultAction: init.defaultAction,
			defaultActionCtx: this.actContext,
			defaultActionOn: init.resHandlingMode === "sel" ? 'userSelChange' : undefined
		};

		if (init.grid) Object.assign(gridInit, init.grid);

		sr.appendChild(this.grid.initialize(gridInit));

		this.grid.addEventListener('grid-select', function (this: IGrid, ev: Event) {
			const utBrowser = DOMSH.findHost<UtBrowser>(this);
			const sel = utBrowser.currentDatas.getSelectedDatas();
			const resList = utBrowser.actContext.resList;
			resList.length = 0;
			for (let r of sel) if (!isNodeAutoFold(r)) resList.push(r);
			utBrowser._refreshActContext();
			utBrowser.onSelChange.emit(utBrowser, resList);
			if (this.matches(':focus')) this.dispatchEvent(new CustomEvent<IFocusActionables<IDepotCtx>>('c-focus-actions', {detail: utBrowser, bubbles: true, composed: true}));
		});

		this.grid.addEventListener('focus', function (this: IGrid, ev: Event) {
			this.dispatchEvent(new CustomEvent<IFocusActionables<IDepotCtx>>('c-focus-actions', {detail: DOMSH.findHost<UtBrowser>(this), bubbles: true, composed: true}));
		});

		this.grid.linesNode.setAttribute("draggable", "true");
		this.grid.linesNode.addEventListener('dragstart', function (ev: DragEvent) {
			//console.log(ev);
			const me = DOMSH.findHost<UtBrowser>(DOMSH.findHost<IGrid>(this))
			if (me.actContext.resList.length > 0) {
				const resTypes = me.reg.env.resTypes;
				const root = me.reg.env.universe.urlTree.url.url;
				const paths = me.actContext.resList.map((n) => URLTREE.concatUrl(root, resTypes.getResTypeFor(n).livePath(n.permaPath)));
				ev.dataTransfer.effectAllowed = "all";
				const data = paths.join('\r\n');
				ev.dataTransfer.setData("text/plain", data);
				ev.dataTransfer.setData("text/uri-list", paths[0]); //bug https://bugs.chromium.org/p/chromium/issues/detail?id=239745
				ev.dataTransfer.setData("text/x-uri-list", data); // contournement bug https://bugs.chromium.org/p/chromium/issues/detail?id=239745
				const img = new Image();
				img.src = me.reg.env.universe.config.skinUrl.resolve("store/objects/res/default.svg").url;
				ev.dataTransfer.setDragImage(img, 12, 12);
			}
		});

		this._initBarUi(init);

		if (init.accelKeyMgr) {
			this.accelKeyMgr = init.accelKeyMgr;
			this.grid.addEventListener('keydown', (ev: KeyboardEvent) => {
				this.accelKeyMgr.handleKeyboardEvent(ev, this.actContext);
			});
		}

		let actions = init.actions;
		if (!actions && this.hasAttribute("actions")) actions = this.reg.mergeLists(...BASIS.extractAttr(this, 'actions').split(' '));
		if (actions) {
			actions = ACTION.injectSepByGroup(actions, this.reg.getPref("groupOrder.store.res", ""), this.actContext);
			this.actions = actions;
			this.focusActionables = ActionBtn.buildButtons(actions, this.actContext, 'bar');
		}

		return this.datasFull.initDataFull().then(() => {
			if (this.resHandling) {
				const req = new InfoReqCurrentRes();
				this.resHandling.dispatchInfo(req, this);
				this.setCurrentRes(req.resPath);
				this.resHandling.addConsumer(this);
			}
		});
	}

	protected _initBarUi(init: OUtBrowserInit) {
		if (!init.actionsStart && init.hideSearch && !init.actionsEnd) return;
		const header = this.grid.insertAdjacentElement("beforebegin", <header/>);
		if (init.actionsStart) {
			const ctx = BASIS.newInit(init.actionsStart, this.reg);
			ctx.actionContext = this.actContext;
			const bar = header.appendChild(<BarActions id="actionsStart" î={ctx}/>) as BarActions<IUtBrowserCtx>;
			this.onSelChange.add(bar.refresh.bind(this));
		}
		if (!init.hideSearch) this._initSearchUi(header, init);
		if (init.actionsEnd) {
			const ctx = BASIS.newInit(init.actionsEnd, this.reg);
			ctx.actionContext = this.actContext;
			const bar = header.appendChild(<BarActions id="actionsEnd" î={ctx}/>) as BarActions<IUtBrowserCtx>;
			this.onSelChange.add(bar.refresh.bind(this));
		}
	}

	protected _initSearchUi(parent: Node, init: OUtBrowserInit) {
		this.search = <input type="search" placeholder="Filtrer..." aria-label="Filtrer" spellcheck="false" onchange={this.onSearchChange} oninput={this.onSearchChange} onkeydown={this.onSearchKeydown}/> as HTMLInputElement;
		parent.appendChild(<div id="search">{this.search}</div>);
	}

	protected _refreshActContext() {
		const resList = this.actContext.resList;
		//resCurrent = ligne sel si 1 entrée sel, sinon root de l'utBrowser.
		const resCurrent = resList.length === 1 ? resList[0] : this.datasFull.getDataByOffset(0) as JNodeShortProps;
		if (resCurrent) {
			this.actContext.reg = this.reg.env.universe.resTypes.getResTypeFor(resCurrent).newShortResRegFromDepotReg(this.reg, resCurrent);
		} else {
			//pas de sel ni de root chargé, registre du utBrowser : IDepotShortResUiEnv null ou le registre du browser ref déjà une re courante.
			this.actContext.reg = this.reg as IReg<IDepotUiEnv & IDepotShortResUiEnv>
		}
	}

	/** Retourne true si différente. */
	protected buildSearchReq(): boolean {
		const req = this.datasSearch.request;
		//this.datasSearch.request = `nameStart(${this.search.value})`;
		//Note : en mode full isResRoot est implémenté coté js via UtBrowserDataBase.isResRoot().
		this.datasSearch.request = `isResRoot&nameMatch((?i^).*${LANG.escape4RegexpFuzzy(this.search.value).replace(/[\^\)]/g, '^$&')}.*)`
		return req !== this.datasSearch.request;
	}

	protected gotoModeSearch() {
		//if(this.modeSearch) return;
		if (!this.datasSearch) {
			this.datasSearch = new UtBrowserDataSearch(this.datasFull); //TODO UtBrowserDataSearchLive
			//TODO LIVE this.datasSearch.initDataSearchLive(this.place);
		} else {
			this.datasSearch.resetDatas();
		}
		this.search.classList.toggle("filtered", true);
		this.grid.classList.toggle("filtered", true);
		this.grid.dataHolder = this.datasSearch;
		this.buildSearchReq();
		this.datasSearch.fetchSearchStart();
	}

	protected gotoModeFull() {
		//if(!this.modeSearch) return;
		this.search.classList.toggle("filtered", false);
		this.grid.classList.toggle("filtered", false);
		this.search.value = "";
		this.grid.dataHolder = this.datasFull;
	}

	/** Mode actuellement affiché. */
	get modeSearch(): boolean {return this.grid.dataHolder === this.datasSearch};

	protected onSearchChange(this: HTMLInputElement, ev: Event) {
		const me = DOMSH.findHost<UtBrowser>(this);
		if (me.modeSearch) {
			if (me.search.value.length === 0) {
				//Préservation de la res courante
				me.gotoModeFull();
				if (me.infoBroker) {
					const currentRes = new InfoReqCurrentRes();
					me.infoBroker.dispatchInfo(currentRes, me);
					if (currentRes.resPath) me.setCurrentRes(currentRes.resPath);
				}
			} else {
				if (me.buildSearchReq()) me.datasSearch.fetchSearchStart();
			}
		} else {
			if (me.search.value.length > 0) me.gotoModeSearch();
		}
	}

	onSearchKeydown(this: HTMLInputElement, ev: KeyboardEvent) {
		if (ev.key === "ArrowDown") {
			const me = DOMSH.findHost<UtBrowser>(this);
			if (me.grid.dataHolder.countRows() > 0) {
				me.grid.setSelectedRows(0);
				me.grid.focus();
				ev.stopImmediatePropagation();
				ev.preventDefault();
			}
		}
	}

	async ensureRowVisible(resPath: resPath): Promise<boolean> {
		await this.currentDatas?.openToTarget(resPath);
		const rowKey = this.currentDatas.findRowKeyByResPath(resPath);
		if (rowKey) this.grid.ensureRowVisible(this.currentDatas.getOffset(rowKey));
		return rowKey != null;
	}

	onViewHidden(closed?: boolean) {
		if (closed) {
			this.resHandling?.removeConsumer(this);
			this.datasFull?.closeData();
			this.datasSearch?.closeData();
			if (this.autoCloseReg) this.reg.close();
		}
	}

	get ctxMenuActions() {return {actions: this.actions, actionContext: this.actContext, rect: this.grid.getSelRect()}}

	onInfo(info: IInfo): void {
		if (info instanceof InfoCurrentRes) {
			this.setCurrentRes(info.resPath);
		}
	}

	selectRes(resPath: resPath | null) {
		const targetPath = resPath; //URLTREE.extractUnversionedPath(resPath); //XXX à voir utBrowser avec versions
		if (resPath != null) {
			this.currentDatas.openToTarget(targetPath).then(() => {
				const node = this.currentDatas.findRowKeyByResPath(targetPath);
				if (node) {
					const offset = this.currentDatas.getOffset(node);
					this.grid.setSelectedRows(offset);
					this.grid.ensureRowVisible(offset);
				}
			});
		} else {
			this.grid.clearSel();
		}
	}

	setCurrentRes(resPath: resPath | null) {
		if (this.resHandlingBySel) {
			this.selectRes(resPath);
		} else {
			//console.log("setCurrentRes:::", resPath);
			this._currentPath = resPath;
			this.grid.invalidateRows();
			if (this._currentPath) {
				this.ensureRowVisible(this._currentPath);
			}
		}
	}

	/** */
	redrawLine(row: GridDataRowJson<JNodeUtBrowser>, line: HTMLElement): void {
		redrawResLine(this.reg, row as GridDataRowJson<JNodeShortProps>, line, this._currentPath);
	}

	_currentPath: resPath = null;


	/** cf UtBrowserDataBase.callbackFetching. */
	onFetching(state: 'fetchInit' | 'fetchNext' | 'fetchEnd') {
		//window.requestIdleCallback(() => { NON ordre d'appel fetchInit -> fetchEnd pas toujours garanti (Chrome/69 avec debugger).
		// if (this._searchingMsg) {
		// 	this._searchingMsg.remove();
		// 	this._searchingMsg = null;
		// }
		// if (state === 'fetchInit') {
		// 	this._searchingMsg = this.shadowRoot.insertBefore(<div id="fetchInit">Recherche...</div>, this.grid);
		// } else if (state === 'fetchNext') {
		// 	this._searchingMsg = this.shadowRoot.insertBefore(<div id="fetchNext">Recherche suivants...</div>, this.grid.nextSibling);
		// }
	}
}


REG.reg.registerSkin('store-ut-browser', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		position: relative;
	}

	header {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: row;
		align-items: center;
	}

	#search {
		flex: 1;
		background: .1em / 1em no-repeat url(/@skin@/commons/icons/filter.svg) var(--form-search-bgcolor);
		padding: 2px;
		padding-inline-start: 1.2em;
		border-bottom: 1px solid var(--border-color);
	}

	input {
		padding: 2px;
		background: var(--form-search-bgcolor);
		color: var(--form-color);
		border: none;
		width: 100%;
		font-size: inherit;
	}

	.filtered {
		background: url(/@skin@/commons/icons/filtered.svg) var(--row-bgcolor);
	}

	input:focus {
		outline: var(--focus-outline);
	}

	input::placeholder {
		color: var(--fade-color);
		letter-spacing: 2px;
		font-size: .8em;
		font-style: italic;
	}

	input:focus::placeholder {
		color: transparent;
	}

	c-grid {
		flex: 1;
		border: none;
	}

	#fetchInit, #fetchNext {
		position: absolute;
		z-index: 1;
		background-color: var(--bgcolor);
		color: var(--color);
		align-self: center;
		padding: .5em;
		border: 1px solid var(--border-color);
		border-radius: 1em;
	}

	#fetchInit {
		top: 2em;
	}

	#fetchNext {
		bottom: 1em;
	}
`);

REG.reg.registerSkin('store-ut-browser/grid', 1, /* language=CSS */ `
	.current {
		background-position: right;
		background-repeat: no-repeat;
		background-image: var(--row-current-img-end);
		background-color: var(--row-current-bgcolor);
	}

	.unlisted > .cell > span {
		background: no-repeat right / .7em url(/@skin@/store/states/limited.svg);
		padding-inline-end: 1em;
	}

	.trashed > .cell > span {
		background: no-repeat right / .7em url(/@skin@/store/states/invisible.svg);
		padding-inline-end: 1em;
	}

	.line {
		padding: .1em .3em;
	}
`);

customElements.define('store-ut-browser', UtBrowser);


/**
 * Gestionnaire des données du grid (arbre json des noeuds de l'urlTree).
 */
export abstract class UtBrowserDataBase extends GridDataHolderJsonTree<JNodeUtBrowser> {

	hasAnyVCB: boolean;

	/** Callback pour retour graphique d'un chargement/recherche en cours. */
	callbackFetching: { onFetching: (state: 'fetchInit' | 'fetchNext' | 'fetchEnd') => void };

	lastError: undefined /* initing*/ | null /* pas d'erreur */ | any;

	/** Séquentialisation des requetes pour éviter des races conditions. */
	sequentialReq: Promise<any>;

	/** listeners des chgt de datas. */
	protected _resChange: (m: JStoreChange) => void;
	protected _connOpened: () => void;

	constructor(public readonly fetcher: IUtBrowserFetcher, public readonly reg: IReg<IDepotUiEnv>, public pathRoot: resPath = URLTREE.DEFAULT_PATH_ROOT, public jsNodeFilter?: (data: JNodeShortProps) => boolean) {
		super('ch');
		this.hasAnyVCB = reg.env.resTypes.hasAnyVCB();
		this.setNaturalOrder(CellBuilderUtBrowser.makeSortFn(fetcher));
	}

	resetDatas() {
		this.lastError = undefined;
		this.setDatas([]);
	}

	/**
	 *
	 * @param lastAncestor si true, remonte le JNodeTree OU le JNodeAutoFold le plus proche possible de resPath.
	 */
	findRowKeyByResPath(resPath: resPath): JNodeTreeB;
	findRowKeyByResPath(resPath: resPath, lastAncestor: boolean): JNodeUtBrowser;
	findRowKeyByResPath(resPath: resPath, lastAncestor?: boolean): JNodeUtBrowser {
		if (URLTREE.isQueryPath(resPath)) return null;
		if (!URLTREE.isDescendantPathOrEqual(this.pathRoot, resPath)) return null;
		if (resPath === this.pathRoot) return this._datas[0];
		let offset = this.pathRoot.length + 1;
		let nextEnd = resPath.indexOf('/', offset);
		let children = this.ch(this._datas[0]);
		if (!children) return;
		let previous: JNodeUtBrowser = this._datas[0];
		while (nextEnd > 0) {
			//On n'est pas sur le dernier fragment
			let parentNm = resPath.substring(offset, nextEnd);
			const idxV = parentNm.indexOf('@');
			let parentV: string;
			if (idxV >= 0) {
				parentV = parentNm.substring(idxV + 1);
				parentNm = parentNm.substring(0, idxV);
			}
			let parent: JNodeTreeB;
			for (let i = 0; children && i < children.length; i++) {
				const ch = children[i];
				if (isNodeAutoFold(ch)) {
					if (this.fetcher.naturalSortFn(ch.from, parentNm) <= 0 && this.fetcher.naturalSortFn(ch.to, parentNm) > 0) {
						//trouvé le bon autoFold, on s'enfonce
						previous = ch;
						children = this.ch(ch);
						i = -1;
					}
				} else if (ch.n === parentNm) {
					parent = ch;
					break;
				}
			}
			if (!parent) return lastAncestor ? previous : null;
			previous = parent;
			children = this.ch(parent);
			if (parent.vcb === "n") {
				//On cherche la bonne version dans les noeuds fils
				if (!children) return lastAncestor ? previous : null; //previous de type vcb="n" est le noeud le plus précis valide.
				parent = null;
				for (let i = 0; children && i < children.length; i++) {
					const ch = children[i];
					if (!isNodeAutoFold(ch) && ch.v === parentV) {
						parent = ch;
						children = this.ch(parent);
						break;
					}
				}
				if (!parent) return lastAncestor ? previous : null; //previous de type vcb="n" est le noeud le plus précis valide.
				previous = parent;
			}
			if (!children) return lastAncestor ? parent : null;
			offset = nextEnd + 1;
			nextEnd = resPath.indexOf('/', offset);
		}

		//Dernier fragment du path
		let nm = resPath.substring(offset);
		const idxV = nm.indexOf('@');
		let parentV: string;
		if (idxV >= 0) {
			parentV = nm.substring(idxV + 1);
			nm = nm.substring(0, idxV);
		}
		for (let i = 0; children && i < children.length; i++) {
			const ch = children[i];
			if (isNodeAutoFold(ch)) {
				if (this.fetcher.naturalSortFn(ch.from, nm) <= 0 && this.fetcher.naturalSortFn(ch.to, nm) > 0) {
					//trouvé le bon autoFold, on s'enfonce
					previous = ch;
					children = this.ch(ch);
					i = -1;
				}
			} else if (ch.n === nm) {
				if (ch.vcb === "n") {
					children = this.ch(ch);
					// on cherche la bonne version dans les fils
					if (children) for (let i = 0; i < children.length; i++) {
						const chV = children[i];
						if (!isNodeAutoFold(chV) && chV.v === parentV) return chV;
					}
					return lastAncestor ? ch : null; //ch de type vcb="n" est le noeud le plus précis valide.
				}
				return ch;
			}
		}

		return lastAncestor ? previous : null;
	}

	async openToTarget(resPath: resPath): Promise<void> {
		for (; ;) {
			const node: JNodeUtBrowser = this.findRowKeyByResPath(resPath, true);
			if (node) this.forceRowKeyShown(node);
			if (node != null && (isNodeAutoFold(node) || node.vcb === "n" || node.permaPath !== resPath)) {
				if (!await this.openFolderAsync(node)) return;
			} else {
				return;
			}
		}
	}

	async openToTargets(...resPaths: resPath[]): Promise<void> {
		for (let p of resPaths) await this.openToTarget(p);
	}

	// async openToTargets(resPaths: resPath[]): Promise<void> {
	// 	while (this.sequentialReq) await this.sequentialReq;
	// 	if (DEBUG) console.log("utBrowser.openToTargetsSeq:::start", resPaths);
	// 	const roots = this.findRootsToFetchOrOpenAnc(resPaths);
	// 	try {
	// 		if (roots.length === 0) return;
	// 		if (roots.length === 1) return await (this.sequentialReq = this._openToTargets(roots[0].root, roots[0].targets));
	// 		await (this.sequentialReq = Promise.all(roots.map((root) => this._openToTargets(root.root, root.targets))));
	// 	} finally {
	// 		this.sequentialReq = null;
	// 		if (DEBUG) console.log("utBrowser.openToTargetsSeq:::end", roots);
	// 	}
	// }
	//
	// /** Ne pas utiliser directement, cf openToTargets(); */
	// protected async _openToTargets(rowKey: JNodeUtBrowser, targets: resPath[]): Promise<void> {
	// 	try {
	//
	// 		const datas = await this.fetcher.fetchNodes(rowKey.path, targets);
	// 		if (!this.isRowkeyAlive(rowKey)) return; //modification de l'arbre entre temps, noeud perdu.
	// 		if (this.nodeFilter) {
	// 			const filter = (datas: JSrcFieldsTree) => {
	// 				if (datas.ch) datas.ch.filter(this.nodeFilter).forEach(filter);
	// 			};
	// 			filter(datas);
	// 		}
	// 		if (!datas.ch) datas.ch = [];//reeval si datas est toujours un dossier ?
	// 		const sortFn = this._grid ? this._grid.sortFn : null;
	// 		if (sortFn) this._sortChildren(datas.ch, sortFn, true);
	// 		this.updateDatasInTree(rowKey, 0, rowKey.ch?.length || 0, (data: JSrcFieldsTree) => data.ch != null, ...datas.ch);
	// 		this.openFolder(rowKey);
	// 	} catch (e) {
	// 		POPUP.showNotifError("Accès au serveur impossible", this.grid);
	// 		this.closeFolder(rowKey);
	// 		throw e;
	// 	}
	// }

	// /** Attention targets est modifié par l'algo (=> cloner en amont si besoin) */
	// protected findRootsToFetchOrOpenAnc(targets: resPath[]): { root: JSrcFieldsTree, targets: resPath[] }[] {
	// 	const r = [] as { root: JSrcFieldsTree, targets: resPath[] }[];
	// 	for (let i = 0; i < targets.length; i++) {
	// 		const target = targets[i];
	// 		if (!target) continue;
	// 		const rowKey = this.findRowKeyByResPath(target, true);
	// 		if (rowKey) {
	// 			if (rowKey.path !== target) {
	// 				//trouvé un root à charger
	// 				const newRoot = {root: rowKey, targets: [target]};
	// 				r.push(newRoot);
	// 				for (let k = i + 1; k < targets.length; k++) {
	// 					//On ajoute les autres subUri qui ont ce root
	// 					if (SRC.isSubUri(rowKey.path, targets[k])) {
	// 						newRoot.targets.push(targets[k]);
	// 						targets[k] = null; //traité
	// 					}
	// 				}
	// 			} else {
	// 				//rowKey trouvé, on s'assure que ses ancêtres sont ouverts.
	// 				const parent = this.getRowKeyParent(rowKey);
	// 				if (parent) this.openFolder(parent);
	// 			}
	// 		}
	// 	}
	// 	return r;
	// }

	protected async refreshRowKey(rowKey: JNodeUtBrowser) {
		if (isNodeAutoFold(rowKey)) return; //todo refresh fils ?
		const fields = await this.fetcher.fetchNode(rowKey.vcb ? rowKey.permaPath : URLTREE.extractUnversionedLeafPath(rowKey.permaPath), false);
		if (!this.isRowkeyAlive(rowKey)) return; //modification de l'arbre entre temps, noeud perdu.
		if (fields == null) {
			this.deleteRowKey(rowKey);
		} else {
			// FIXME Si ajout colonnes avec autres critères de tri que le resPath => on doit repositionner au bon index dans l'arbre.
			const newRowKey = Object.assign(Object.create(null), fields);
			if (rowKey.vcb) newRowKey.vcb = rowKey.vcb;
			this.replaceRowKey(rowKey, newRowKey, newRowKey.t != "moved" && newRowKey.t != "repos");
			if (this._grid && this._grid.isRowSelected(this.getOffset(newRowKey))) {
				//on simule une resélection pour update de UtBrowser.resList.
				this._grid.dispatchEvent(new CustomEvent('grid-select', {bubbles: true, composed: true}));
			}
		}
	}

	getParentUtNode(data: JNodeUtBrowser): JNodeTree | null {
		let p = this.parent(data);
		while (isNodeAutoFold(p)) p = this.parent(p);
		return p;
	}

	protected async insertRes(parentRowKey: JNodeUtBrowser | null, resPath: resPath) {
		while (this.sequentialReq) await this.sequentialReq;
		try {
			if (parentRowKey && !this.isRowkeyAlive(parentRowKey)) return;
			await (this.sequentialReq = this._insertRes(parentRowKey, resPath));
		} finally {
			this.sequentialReq = null;
		}
	}

	protected async _insertRes(parentRowKey: JNodeUtBrowser, resPath: resPath) {
		const fields = await this.fetcher.fetchNode(resPath);
		if (fields == null || (this.jsNodeFilter && !isNodeAutoFold(fields) && !this.isResRoot(parentRowKey, fields) && !this.jsNodeFilter(fields))) return;
		if (parentRowKey && !this.isRowkeyAlive(parentRowKey) && this.getRowKeyFolderState(parentRowKey) !== EFolderState.opened) return; //modification de l'arbre entre temps, noeud perdu.
		if (this.findInChildren(parentRowKey ? parentRowKey.ch : this._datas, resPath) >= 0) return; //uri insérée entre temps.
		this.insertRowKey(parentRowKey, fields);
	}

	/** Les noeuds fils qui ont le même resId que le parent sont exclus. Correspond au filtre !isResRoot en search. */
	protected isResRoot(parent: JNodeUtBrowser | null, child: JNodeTreeB) {
		if (child.vcb === "v") return true;
		while (isNodeAutoFold(parent)) parent = this.parent(parent);
		return !parent || !child.resId || child.resId !== parent.resId;
	}

	protected filterChildren(parent: JNodeUtBrowser | null, chidlren: JNodeUtBrowser[]) {
		if (this.jsNodeFilter) return chidlren.filter((n: JNodeUtBrowser) => isNodeAutoFold(n) || (this.isResRoot(parent, n) && this.jsNodeFilter(n)));
		return chidlren.filter((n: JNodeUtBrowser) => isNodeAutoFold(n) || this.isResRoot(parent, n));
	}

	protected findInChildren(children: JNodeUtBrowser[], resPath: resPath): number {
		for (let i = 0; i < children.length; i++) {
			const ch = children[i];
			if (isNodeAutoFold(ch)) return -1; //autoFold à la fin, on ne trouveras plus resPath
			if (ch.permaPath === resPath) return i;
		}
		return -1;
	}

	protected findInChildrenByName(children: JNodeUtBrowser[], name: string): JNodeTreeB {
		if (!children) return null;
		for (let i = 0; i < children.length; i++) {
			const ch = children[i];
			if (isNodeAutoFold(ch)) return null; //autoFold à la fin, on ne trouveras plus resPath
			if (ch.n === name) return ch;
		}
		return null;
	}


	protected _initRowData(data: JNodeUtBrowser, parent: JNodeUtBrowser, openIt: (data: any) => boolean): number {
		if (isNodeAutoFold(data)) {
			if (data.ch === undefined) data.ch = null;
		} else {
			if (this.hasAnyVCB) {
				const type = this.reg.env.resTypes.getResTypeFor(data);
				if (type.prcIsFolder && type.prcVersionning === "VCB") {
					if ((parent as JNodeTreeB)?.vcb === 'n') {
						data.vcb = 'v';
						data.permaPath = URLTREE.appendToPath(URLTREE.extractParentPath((parent as JNodeTreeB).permaPath) || this.pathRoot, URLTREE.buildPermaName(data));
					} else {
						data.vcb = 'n';
					}
					if (data.ch === undefined) data.ch = null; //dossier de versions.
				}
			}
			//On construit la prop permaPath
			if (data.permaPath == null) data.permaPath = URLTREE.appendToPath(((isNodeAutoFold(parent) ? this.getParentUtNode(parent) : parent)?.permaPath || this.pathRoot), URLTREE.buildPermaName(data));
			//On force la prop 'ch' pour les res de type folder
			if (data.t === "noContent" || data.t === "home") {
				if (data.ch === undefined) data.ch = null;
			}
		}
		return super._initRowData(data, parent, openIt);
	}

	closeData() {
		const depotEvents = this.reg.env.depotEvents;
		if (!depotEvents) return;
		if (this._resChange) {
			depotEvents.removeListener("resChange", this._resChange);
			this._resChange = null;
		}
		if (this._connOpened) {
			depotEvents.removeListener("connOpened", this._connOpened);
			this._connOpened = null;
		}
	}
}


export class UtBrowserDataFull extends UtBrowserDataBase {

	initDataFull(): Promise<void> {
		const depotEvents = this.reg.env.depotEvents;
		if (depotEvents) {
			this._resChange = (m: JStoreChange) => {
				//console.log("_resChange:::",m);
				const resPath = m.props.path;
				const node = this.findRowKeyByResPath(resPath, true);
				if (!node) return; //hors scope de this.pathRoot
				if (isNodeAutoFold(node) || node.permaPath === URLTREE.extractParentPath(resPath)) {
					//On a trouvé le parent direct
					if (this.isRowKeyShown(node) && this.getRowKeyFolderState(node) === EFolderState.opened) {
						//On insert ce nouvel élément dans son dossier père visible et ouvert
						this.insertRes(node, resPath);
					} else {
						//Le dossier parent ou un de ses ancêtre est clos.
						//On cleanup le contenu du dossier qui devra être rechargé.
						this.resetAsyncChildren(node);
					}
				} else if (node.vcb === "n") {
					//On est sur une version d'un noeud,
					if (this.isRowKeyShown(node) && this.getRowKeyFolderState(node) === EFolderState.opened) {
						//on rafraichit la liste entière des noeuds (algo de tri des version maitrisé par le serveur)
						this.closeFolder(node);
						this.openFolderAsync(node);
					} else {
						//Le dossier parent ou un de ses ancêtre est clos.
						//On cleanup le contenu du dossier qui devra être rechargé.
						this.resetAsyncChildren(node);
					}
				} else if (URLTREE.extractUnversionedLeafPath(node.permaPath) === URLTREE.extractUnversionedLeafPath(resPath)) {
					//node trouvé
					if (this.isRowKeyShown(node)) {
						//Ce noeud est visible.
						this.refreshRowKey(node);
					} else {
						//On cleanup le contenu du dossier parent qui devra être rechargé.
						this.resetAsyncChildren(this.parent(node));
					}
				}
			};

			this._connOpened = () => {
				//TODO this.refetchTree(targets);
				this.fetchRootDatas();
			};
			depotEvents.on("resChange", this._resChange);
			depotEvents.on("connOpened", this._connOpened);
		}
		return this.fetchRootDatas();
	}

	/** Construction par défaut du contenu. */
	protected async fetchRootDatas() {
		while (this.sequentialReq) await this.sequentialReq;
		try {
			await (this.sequentialReq = this._fetchRootDatas());
		} finally {
			this.sequentialReq = null;
		}
	}

	/** Ne pas utiliser directement, cf fetchRootDatas(); */
	protected async _fetchRootDatas() {
		if (DEBUG) console.log("utBrowser.fetchRootDatas:::start");
		if (this.callbackFetching) this.callbackFetching.onFetching('fetchInit');
		this.lastError = undefined;
		try {
			const datas = await this.fetcher.fetchNode(this.pathRoot, true);
			if (datas == null) throw Error("Root not found");
			this.lastError = null;
			datas.ch = this.filterChildren(datas, datas.ch || []);
			this.setDatas([datas]);
			this.openFolder(datas);
		} catch (e) {
			this.lastError = e;
			this.setDatas([]);
			throw e;
		} finally {
			if (this.callbackFetching) this.callbackFetching.onFetching('fetchEnd');
			if (DEBUG) console.log("utBrowser.fetchRootDatas:::end");
		}
	}

	// TODO IUtBrowserFetcher.fetchNodes(pathRoot, pathTargets)
	// /** RE-Construction du contenu en tentant d'afficher targets. */
	// protected async refetchTree(targets: resPath[]) {
	// 	while (this.sequentialReq) await this.sequentialReq;
	// 	try {
	// 		await (this.sequentialReq = this._refetchTree(targets));
	// 	} finally {
	// 		this.sequentialReq = null;
	// 	}
	// }
	//
	// /** Ne pas utiliser directement, cf refetchTree(); */
	// protected async _refetchTree(targets: resPath[]): Promise<void> {
	// 	if (DEBUG) console.log("utBrowser.refetchTree:::start", targets);
	// 	if (this.callbackFetching) this.callbackFetching.onSearching('fetchInit');
	// 	this.lastError = undefined;
	// 	try {
	// 		let scrollTopPath: resPath;
	// 		let scrollTopOffset: number;
	// 		if (this._grid) {
	// 			scrollTopOffset = this._grid.getVisibleOffsetStart();
	// 			while(scrollTopOffset>0) {
	// 				const d = this.getRowKey(scrollTopOffset);
	// 				if(!isNodeAutoFold(d)) {
	// 					scrollTopPath = d.path;
	// 					break;
	// 				}
	// 				scrollTopOffset--;
	// 			}
	// 		}
	// 		const datas = await this.fetcher.fetchNodes(this.pathRoot, targets);
	// 		this.lastError = null;
	// 		if (!datas.ch) datas.ch = [];
	// 		if (this.nodeFilter) {
	// 			const filter = (datas: JNodeUtBrowser) => {
	// 				if (datas.ch) datas.ch.filter(this.nodeFilter).forEach(filter);
	// 			};
	// 			filter(datas);
	// 		}
	// 		const memOpenStates = this.defaultOpenState;
	// 		try {
	// 			this.defaultOpenState = (data: JSrcFieldsTree) => data.ch != null;
	// 			if (this.showRoot) {
	// 				this.root = null;
	// 				this.setDatas([datas]);
	// 				this.openFolder(datas);
	// 			} else {
	// 				this.root = datas;
	// 				this.setDatas(datas.ch || []);
	// 			}
	// 			if (this._grid) {
	// 				const newRowKey = scrollTopPath ? this.findRowKeyByResPath(scrollTopPath) : null;
	// 				if (newRowKey) {
	// 					//resPath retrouvé, on scroll
	// 					this._grid.ensureRowVisible(this.getOffset(newRowKey), {block: "start", behavior: "auto"});
	// 				} else {
	// 					//resPath pas retrouvé on repossitionne dans l'absolu
	// 					this._grid.ensureRowVisible(scrollTopOffset, {block: "start", behavior: "auto"});
	// 				}
	// 			}
	// 		} finally {
	// 			this.defaultOpenState = memOpenStates;
	// 		}
	// 	} catch (e) {
	// 		this.lastError = e;
	// 		this.setDatas([]);
	// 		throw e;
	// 	} finally {
	// 		if (this.callbackFetching) this.callbackFetching.onSearching('fetchEnd');
	// 		if (DEBUG) console.log("utBrowser.refetchTree:::end", targets);
	// 	}
	// }

	async openFolderAsync(rowKey: JNodeUtBrowser): Promise<boolean> {
		try {
			if (DEBUG) console.log("openFolderAsync:::start", rowKey);
			if (this.folderSt(rowKey) !== EFolderState.closed) return false;
			this.setFolderSt(rowKey, EFolderState.opening);
			let datas: JNodeUtBrowser[];
			if (isNodeAutoFold(rowKey)) {
				datas = await this.fetcher.fetchFoldedChildren(this.getParentUtNode(rowKey).permaPath, rowKey.from, rowKey.to);
			} else if (rowKey.vcb === 'n') {
				datas = await this.fetcher.fetchVersions(rowKey.permaPath);
				datas.forEach((n: JNodeTreeB) => {
					n.vcb = 'v';
					n.n = rowKey.n;
				});
			} else {
				const node = await this.fetcher.fetchNode(rowKey.permaPath, true);
				datas = node?.ch;
			}
			if (!this.isRowkeyAlive(rowKey)) return false; //modification de l'arbre entre temps, noeud perdu.
			datas = this.filterChildren(rowKey, datas || []);
			this.setChilrenTo(rowKey, datas, this.defaultOpenState);
			this.setFolderSt(rowKey, EFolderState.closed);
			return this.openFolder(rowKey);
		} catch (e) {
			POPUP.showNotifError("Accès au serveur impossible", this.grid);
			this.closeFolder(rowKey);
			throw e;
		} finally {
			if (DEBUG) console.log("openFolderAsync:::end", rowKey);
		}
	}
}


export class UtBrowserDataSearch extends UtBrowserDataBase {

	root: JNodeTree;

	_datas: JNodeTree[];

	/** Chargement progressif. Donc impl Grid obligatoire (ie pas SmallGrid). */
	_grid: Grid;

	request: string;

	/** Requete en cours (pour gérer l'attente avant insertion dans l'arbre d'une détection d'un update d'une res)*/
	protected _currentSearch: Promise<void>;

	/** Dernier noeud remonté permettant de fixer le point de départ de la prochaine recherche. */
	protected _afterPath: resPath;

	constructor(fromFull: UtBrowserDataFull) {
		super(fromFull.fetcher, fromFull.reg, fromFull.pathRoot, fromFull.jsNodeFilter);
		this.callbackFetching = fromFull.callbackFetching;
		this.defaultOpenState = GridDataHolderJsonTree.defaultOpened;
	}

	initDataSearch() {
		const depotEvents = this.reg.env.depotEvents;
		if (depotEvents) {
			this._resChange = (m: JStoreChange) => {
				const resPath = m.props.path;
				if (!URLTREE.isDescendantPath(this.pathRoot, resPath)) return;
				const node = this.findRowKeyByResPath(resPath);
				if (!node) return;
				//node trouvé
				if (this.isRowKeyShown(node)) {
					//Ce noeud est visible.
					return this.refreshRowKey(node);
				} else {
					//On cleanup le contenu du dossier parent qui devra être rechargé.
					this.resetAsyncChildren(this.parent(node));
				}
			};

			this._connOpened = () => {
				this.fetchSearchStart();
			};
			depotEvents.on("resChange", this._resChange);
			depotEvents.on("connOpened", this._connOpened);
		}
	}

	connectToGrid(grid: IGrid | null) {
		super.connectToGrid(grid);
		this.listenForNextSearch();
	}

	fetchSearchStart() {
		if (this._currentSearch) {
			const req = this.request;
			this._currentSearch.then(() => {if (req === this.request) this.fetchSearchStart()});
		} else {
			this._currentSearch = this._fetchSearchStart();
		}
	}

	fetchSearchNext() {
		if (!this._afterPath || !this._grid) return;
		if (this._currentSearch) return; //(sécurité, ne devrait pas arriver)
		this._currentSearch = this._fetchSearchNext();
	}

	/**
	 * Dossiers en search pas refermables : icompatible avec algo actuel de chargement progressif,
	 * si this._afterPath se trouve dans le dossier refermé et que le fetch suivant ne fournit pas des entrées
	 * au delà de ce dossier refermé, l'affichage est bloqué, les frères suivants du dossier refermé ne sont
	 * jamais affichés. XXX Algo this._afterPath à revoir pour permettre l'ouverture : _afterPath mémorisé
	 * dans chaque dossier + injection d'intersectObservers dans les dossiers pour les compléter s'ils sont
	 * réouverts ultérieurement...
	 */
	closeFolder(rowKey: any): boolean {return false}

	isRowKeyFolderStateSwitchable(rowKey: any): boolean {return false}

	/** Retourne null si aucune requete en cours de traitement. */
	get searching(): Promise<void> {return this._currentSearch}

	protected async _fetchSearchStart() {
		if (this.callbackFetching) this.callbackFetching.onFetching('fetchInit');
		this.lastError = undefined;
		this.unlistenForNextSearch();
		try {
			const results = await this.fetcher.searchNodes(this.pathRoot, this.request);
			if (!this._grid) return; //affichage clos.
			//console.log("_fetchSearchStart:::", results);
			this._afterPath = results.next;
			this.lastError = null;
			if (!results.found) {
				this.root = null;
				this.setDatas([]);
			} else {
				this.root = results.found;
				this.setDatas(results.found.ch, GridDataHolderJsonTree.defaultOpened);
			}
		} catch (e) {
			this.lastError = e;
			this.setDatas([]);
			throw e;
		} finally {
			this._currentSearch = null;
			if (this.callbackFetching) this.callbackFetching.onFetching('fetchEnd');
		}
		this.listenForNextSearch();
	}

	protected _initRowData(data: JNodeUtBrowser, parent: JNodeUtBrowser, openIt: (data: any) => boolean): number {
		if (this.jsNodeFilter && data.ch) data.ch = data.ch.filter(this.jsNodeFilter);
		return super._initRowData(data, parent, openIt);
	}

	protected unlistenForNextSearch() {
		if (this._grid) this._grid.reachedLimitsCb = null;
	}

	protected listenForNextSearch() {
		if (this._afterPath && this._grid) this._grid.reachedLimitsCb = (start) => {
			if (start) return;
			this.fetchSearchNext();
		};
	}

	protected async _fetchSearchNext(): Promise<void> {
		const grid = this.grid;
		try {
			grid?.freezeRefresh();
			this.lastError = undefined;
			this.unlistenForNextSearch();
			if (this.callbackFetching) this.callbackFetching.onFetching('fetchNext');
			const results = await this.fetcher.searchNodes(this.pathRoot, this.request, this._afterPath);
			if (!this._grid) return; //affichage clos.
			//console.log("_fetchSearchNext:::", results);
			this._afterPath = results.next;
			this.lastError = null;
			let parent = this.root || this._datas[0];
			if (parent && results.found?.ch) for (const child of results.found.ch) {
				this.insertInTree(parent, child);
			}
		} catch (e) {
			this.lastError = e;
			this.setDatas([]);
			throw e;
		} finally {
			this._currentSearch = null;
			grid?.unfreezeRefresh();
			if (this.callbackFetching) this.callbackFetching.onFetching('fetchEnd');
		}
		this.listenForNextSearch();
	}

	protected insertInTree(parent: JNodeTree, toInsert: JNodeTree) {
		const existNode = this.findInChildrenByName(parent.ch, toInsert.n);
		if (existNode) {
			//Noeud trouvé, on fusionne
			if (existNode.vcb === "n") {
				//Cas particulier des versions
				if (toInsert.ch) for (const versNode of toInsert.ch) {
					const existV = this.findInChildrenByVersion(existNode.ch, versNode.v);
					if (existV) {
						//Noeud version trouvé, on fusionne
						if (versNode.ch) for (const child of versNode.ch) {
							this.insertInTree(existV, child);
						}
					} else {
						//Noeud version non trouvé, on insère cette version
						this.insertRowKey(existNode, versNode);
					}
				}
			} else {
				//Noeud "standard"
				if (toInsert.ch) for (const child of toInsert.ch) {
					this.insertInTree(existNode, child);
				}
			}
		} else {
			//Noeud non trouvé, on l'insère
			this.insertRowKey(parent, toInsert);
		}
	}

	protected findInChildrenByVersion(children: JNodeUtBrowser[], version: string): JNodeTreeB {
		if (!children) return null;
		for (let i = 0; i < children.length; i++) {
			const ch = children[i] as JNodeTreeB;
			if (ch.v === version) return ch;
		}
		return null;
	}
}


/**
 * Implémentation par défaut de l'obtention d'un svc d'urlTree.
 */
export class UtBrowserFetcher implements IUtBrowserFetcher {

	maxChildren = 250;

	nodeProps = RES.NODEPROPS_short;

	/** Doit être de la forme "&childrenFilter=..." */
	protected childrenFilter: string = '';

	protected seachFilter: string = undefined;

	constructor(public urlTreeSrv: UrlTreeSrv) {}

	/**
	 * Filre pour lister les fils et la recherche cf eu.scenari.urltree.util.UrlTreeNodePredicates.
	 * Exemples :
	 * - "isFolder"
	 * - "isFolder & !inTrash & !unlisted &!moved"
	 * - "usable" ( = !inTrash & !unlisted &!moved)
	 * - "regexpPrc(img.*) & !inTrash"
	 * - "prc(image,audio,video) & !inTrash"
	 * - "" : pas de filtre mais forcera le mode un seul dossier virtuel lors du listing des fils.
	 * - null : pas de filtre
	 *
	 * @see eu.scenari.urltree.util.UrlTreeNodePredicates
	 */
	setFilter(filter: string): this {
		this.childrenFilter = filter != null ? `&childrenFilter=${encodeURIComponent(filter)}` : '';
		this.seachFilter = filter ? filter : undefined;
		return this;
	}

	async fetchNode(path: resPath, withChildren?: boolean): Promise<JNodeUtBrowser | null> {
		if (withChildren) return this.urlTreeSrv.listChildren(path, `&excludeAll&props=${this.nodeProps}&childrenProps=${this.nodeProps}&childrenAutoFold=${this.maxChildren}${this.childrenFilter}`);
		if (!this.seachFilter) return this.urlTreeSrv.nodeInfos(path, `&excludeAll&props=${this.nodeProps}`);
		const node = await this.urlTreeSrv.nodeInfos(path, `&excludeAll&props=${this.nodeProps}&matchFilter=${this.seachFilter}`);
		return node?.matchFilter === false ? null : node;
	}

	async fetchFoldedChildren(path: resPath, from: string, to?: string): Promise<JNodeUtBrowser[]> {
		const parent = await this.urlTreeSrv.listChildren(path, `&excludeAll&props=path&childrenProps=${this.nodeProps}&childrenAutoFold=${this.maxChildren}${this.childrenFilter}&childrenFrom=${encodeURIComponent(from)}&childrenTo=${encodeURIComponent(to || '')}`);
		return parent.ch || [];
	}

	async fetchVersions(path: resPath): Promise<JNodeUtBrowser[]> {
		const parent = await this.urlTreeSrv.nodeInfos(URLTREE.extractUnversionedLeafPath(path), `&excludeAll&props=${this.nodeProps}&historyProps=${this.nodeProps}`);
		let r: JNodeUtBrowser[];
		if (parent.hist) {
			r = parent.hist;
			parent.hist = undefined;
			r.splice(0, 0, parent);
		} else {
			r = [parent];
		}
		return r;
	}

	searchNodes(root: resPath, exp: string, from?: resPath | undefined): Promise<JSearchNodesResult> {
		return this.urlTreeSrv.searchNodes(root, this.seachFilter ? `${this.seachFilter}&(${exp})` : exp, 50, from);
	}

	get naturalSortFn() {return this.urlTreeSrv.naturalSortPathFn}

	get nameSortFn() {return this.urlTreeSrv.naturalSortNameFn}

	get versionSortFn() {return this.urlTreeSrv.naturalSortVersionFn}
}


/** Gestion du drop dans l'arbre. */
const rowDropMgr = {
	onDragOverRow(ev: DragEvent, grid: IGrid, row: GridDataRowJson<JNodeUtBrowser>, line: HTMLElement): EGridDropPos | EGridDropPos {
		ev.dataTransfer.dropEffect = 'none';
		if (!row) return EGridDropPos.none; //todo ? autoriser sur Root si utBrowser n'affiche pas le folder root "Accueil" ?
		const utBrowser = DOMSH.findHost<UtBrowser>(grid);
		const node = row.rowDatas;
		if (isNodeAutoFold(node)) return EGridDropPos.none;
		if (ev.dataTransfer.types.indexOf('Files') >= 0) {
			const resType = utBrowser.reg.env.resTypes.getResTypeFor(node);
			if (!resType.prcIsFolder) return EGridDropPos.none;
			ev.dataTransfer.dropEffect = 'copy';
			return EGridDropPos.over;
		}
		return EGridDropPos.none;
	},

	async dropOnRow(ev: DragEvent, grid: IGrid, row: GridDataRowJson<JNodeUtBrowser>, line: HTMLElement, pos: EGridDropPos) {
		const utBrowser = DOMSH.findHost<UtBrowser>(grid);
		if (ev.dataTransfer.types.indexOf('Files') >= 0) {
			const node = row.rowDatas;
			if (isNodeAutoFold(node)) return;
			const resType = utBrowser.reg.env.resTypes.getResTypeFor(node);
			if (resType.prcIsFolder) {
				const action = new AddByImport().setFiles(await getFilesOnDrop(ev.dataTransfer));
				const ctx = resType.newShortResRegFromDepotReg(utBrowser.reg, node);
				if (action.isAvailable(ctx)) {
					action.execute(ctx, ev);
				}
			}
		}
	},


} as IGridRowDropMgr;


export class CellBuilderUtBrowser extends CellBuilderResIconName {

	protected _getValue(row: GridDataRowJson<JNodeUtBrowser>): string {
		if (isNodeAutoFold(row.rowDatas)) return "[" + row.rowDatas.from + "...]";
		if (row.rowDatas.vcb === 'v') return row.cacheHolder['resV'] || (row.cacheHolder['resV'] = "@" + this.resTypes.getResTypeFor(row.rowDatas).resVersion(row.rowDatas));
		return super._getValue(row as GridDataRowJson<JNodeShortProps>);
	}

	protected _getIcon(row: GridDataRowJson<JNodeUtBrowser>): string {
		if (isNodeAutoFold(row.rowDatas)) return "/@skin@/store/objects/res/autoFold.svg";
		return super._getIcon(row as GridDataRowJson<JNodeShortProps>);
	}

	redrawCell(row: GridDataRowJson<JNodeUtBrowser>, root: HTMLElement) {
		root.style.fontStyle = isNodeAutoFold(row.rowDatas) ? "italic" : "normal";
		super.redrawCell(row, root);
	}

	// getColSortFn(): IRowSortFn | null {
	// 	return (r1: GridDataRowJson<JNodeUtBrowser>, r2: GridDataRowJson<JNodeUtBrowser>) => {
	// 		return CellBuilderUtBrowser.sortFn(r1.rowDatas, r2.rowDatas);
	// 	}
	// }

	/** Algo de tri des JNodeUtBrowser dans chaque dossier. */
	static makeSortFn(fetcher: IUtBrowserFetcher) {
		return function (n1: JNodeUtBrowser, n2: JNodeUtBrowser) {
			if (isNodeAutoFold(n1)) {
				return isNodeAutoFold(n2) ? fetcher.naturalSortFn(n1.from, n2.from) : 1; //autoFold toujours après les nodes
			} else if (isNodeAutoFold(n2)) {
				return isNodeAutoFold(n1) ? fetcher.naturalSortFn(n1.from, n2.from) : -1; //autoFold toujours après les nodes
			}
			if (n1.vcb === "v") return fetcher.versionSortFn(n1.v, n2.v);
			return fetcher.nameSortFn(n1.n, n2.n);
		}
	}
}

const DEBUG = false; //false